import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Search, Loader2, Send, ExternalLink, Mail, Users } from "lucide-react";
import * as LucideIcons from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "All",
  "Direct Contact",
  "Relationship Building",
  "Value Add",
  "Personal Touch",
  "Social Proof",
  "Problem Solving",
  "Re-engagement",
  "Urgency",
  "Events",
  "Process",
  "Closure",
];

const Techniques = () => {
  const [techniques, setTechniques] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [selectedTechnique, setSelectedTechnique] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [techRes, prospRes] = await Promise.all([
        axios.get(`${API}/techniques`),
        axios.get(`${API}/prospects`),
      ]);
      setTechniques(techRes.data);
      setProspects(prospRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load techniques");
    } finally {
      setLoading(false);
    }
  };

  const getTechniqueIcon = (iconName) => {
    const IconComponent = LucideIcons[iconName];
    return IconComponent ? (
      <IconComponent className="w-6 h-6" />
    ) : (
      <LucideIcons.MessageSquare className="w-6 h-6" />
    );
  };

  const filteredTechniques = techniques.filter((t) => {
    const matchesSearch =
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.description.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = category === "All" || t.category === category;
    return matchesSearch && matchesCategory;
  });

  const openWhatsApp = (template, prospect) => {
    if (!prospect?.phone) {
      toast.error("Selected prospect has no phone number");
      return;
    }
    const phone = prospect.phone.replace(/[^0-9]/g, "");
    const message = template.replace(/{name}/g, prospect.name);
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  const openEmail = (template, prospect) => {
    if (!prospect?.email) {
      toast.error("Selected prospect has no email");
      return;
    }
    const subject = `Following up - ${prospect.name}`;
    const body = template.replace(/{name}/g, prospect.name);
    const url = `mailto:${prospect.email}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
    window.open(url, "_blank");
  };

  const getCategoryColor = (cat) => {
    const colors = {
      "Direct Contact": "bg-blue-900/50 text-blue-400",
      "Relationship Building": "bg-pink-900/50 text-pink-400",
      "Value Add": "bg-emerald-900/50 text-emerald-400",
      "Personal Touch": "bg-purple-900/50 text-purple-400",
      "Social Proof": "bg-amber-900/50 text-amber-400",
      "Problem Solving": "bg-red-900/50 text-red-400",
      "Re-engagement": "bg-cyan-900/50 text-cyan-400",
      Urgency: "bg-orange-900/50 text-orange-400",
      Events: "bg-indigo-900/50 text-indigo-400",
      Process: "bg-slate-700 text-slate-300",
      Closure: "bg-rose-900/50 text-rose-400",
    };
    return colors[cat] || "bg-slate-700 text-slate-300";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gba-green" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-white">
          The 32 Follow-Up Techniques
        </h1>
        <p className="text-slate-400 mt-1">
          Proven methods to stay top of mind and close more deals
        </p>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search techniques..."
            className="pl-10 bg-[#1E293B] border-slate-700 text-white"
            data-testid="technique-search-input"
          />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-full sm:w-48 bg-[#1E293B] border-slate-700 text-white">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent className="bg-[#1E293B] border-slate-700 max-h-80">
            {CATEGORIES.map((cat) => (
              <SelectItem key={cat} value={cat}>
                {cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Techniques Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredTechniques.map((technique, index) => (
          <Card
            key={technique.id}
            className={`bg-[#1E293B] border-slate-700 card-hover cursor-pointer animate-fade-in stagger-${
              Math.min(index + 1, 5)
            }`}
            onClick={() => {
              setSelectedTechnique(technique);
              setSelectedProspect(null);
              setDialogOpen(true);
            }}
            data-testid={`technique-library-card-${technique.id}`}
          >
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gba-green/20 flex items-center justify-center flex-shrink-0 text-gba-green">
                  {getTechniqueIcon(technique.icon)}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-white mb-1 truncate">
                    {technique.name}
                  </h3>
                  <p className="text-sm text-slate-400 line-clamp-2">
                    {technique.description}
                  </p>
                  <span
                    className={`inline-block mt-2 px-2 py-0.5 rounded text-xs font-medium ${getCategoryColor(
                      technique.category
                    )}`}
                  >
                    {technique.category}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredTechniques.length === 0 && (
        <Card className="bg-[#1E293B] border-slate-700 border-dashed">
          <CardContent className="p-12 text-center">
            <Search className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <h3 className="font-heading text-xl text-white mb-2">
              No techniques found
            </h3>
            <p className="text-slate-400">Try adjusting your search or filter</p>
          </CardContent>
        </Card>
      )}

      {/* Technique Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#1E293B] border-slate-700 max-w-lg max-h-[90vh] overflow-y-auto">
          {selectedTechnique && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3 text-white">
                  <div className="w-10 h-10 rounded-full bg-gba-green/20 flex items-center justify-center text-gba-green">
                    {getTechniqueIcon(selectedTechnique.icon)}
                  </div>
                  <div>
                    <span className="block">{selectedTechnique.name}</span>
                    <span
                      className={`text-xs font-normal px-2 py-0.5 rounded ${getCategoryColor(
                        selectedTechnique.category
                      )}`}
                    >
                      {selectedTechnique.category}
                    </span>
                  </div>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                <p className="text-slate-300">{selectedTechnique.description}</p>

                {/* Select Prospect */}
                <div className="space-y-2">
                  <label className="text-sm text-slate-400 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Select a prospect to use this technique
                  </label>
                  <Select
                    value={selectedProspect?.id || ""}
                    onValueChange={(value) => {
                      const p = prospects.find((pr) => pr.id === value);
                      setSelectedProspect(p || null);
                    }}
                  >
                    <SelectTrigger className="bg-[#0F172A] border-slate-700 text-white" data-testid="select-prospect-dropdown">
                      <SelectValue placeholder="Choose prospect..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E293B] border-slate-700 max-h-60">
                      {prospects.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name} {p.company && `- ${p.company}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedProspect && (
                  <div className="bg-slate-800/50 p-3 rounded-lg">
                    <p className="text-sm text-slate-300">
                      <strong>{selectedProspect.name}</strong>
                      {selectedProspect.company && ` at ${selectedProspect.company}`}
                    </p>
                    <div className="flex gap-4 mt-1 text-xs text-slate-500">
                      {selectedProspect.phone && <span>📱 {selectedProspect.phone}</span>}
                      {selectedProspect.email && <span>✉️ {selectedProspect.email}</span>}
                    </div>
                  </div>
                )}

                <Tabs defaultValue="whatsapp">
                  <TabsList className="bg-slate-800">
                    <TabsTrigger value="whatsapp">WhatsApp Template</TabsTrigger>
                    <TabsTrigger value="email">Email Template</TabsTrigger>
                  </TabsList>
                  <TabsContent value="whatsapp" className="mt-3">
                    <div className="bg-slate-800/50 p-4 rounded-lg">
                      <p className="text-sm text-slate-300 whitespace-pre-wrap">
                        {selectedTechnique.whatsapp_template.replace(
                          /{name}/g,
                          selectedProspect?.name || "{name}"
                        )}
                      </p>
                    </div>
                    <Button
                      onClick={() =>
                        openWhatsApp(selectedTechnique.whatsapp_template, selectedProspect)
                      }
                      className="mt-3 w-full bg-green-600 hover:bg-green-700"
                      disabled={!selectedProspect || !selectedProspect.phone}
                      data-testid="technique-send-whatsapp"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Send via WhatsApp
                      <ExternalLink className="w-3 h-3 ml-2" />
                    </Button>
                  </TabsContent>
                  <TabsContent value="email" className="mt-3">
                    <div className="bg-slate-800/50 p-4 rounded-lg">
                      <p className="text-sm text-slate-300 whitespace-pre-wrap">
                        {selectedTechnique.email_template.replace(
                          /{name}/g,
                          selectedProspect?.name || "{name}"
                        )}
                      </p>
                    </div>
                    <Button
                      onClick={() =>
                        openEmail(selectedTechnique.email_template, selectedProspect)
                      }
                      className="mt-3 w-full bg-blue-600 hover:bg-blue-700"
                      disabled={!selectedProspect || !selectedProspect.email}
                      data-testid="technique-send-email"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Send via Email
                      <ExternalLink className="w-3 h-3 ml-2" />
                    </Button>
                  </TabsContent>
                </Tabs>

                {!selectedProspect && (
                  <p className="text-center text-sm text-slate-500">
                    Select a prospect above to use this technique
                  </p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Techniques;
